import { useState } from 'react';
import { formatCutoffWhen, isCancellable, type MyOrder } from '@mealdesk/shared';
import { useT } from '../lib/i18n.js';
import { useCancelOrder } from '../lib/ordering.js';

/**
 * Taking an order back, in two taps.
 *
 * It used to be one, on the screen people reach by tapping an order just to
 * look at it — so looking and cancelling were a careless thumb apart. The
 * second tap is also where the one fact that makes cancelling cheap is said:
 * until the cutoff, the meal can simply be ordered again.
 *
 * Renders nothing for an order that cannot be taken back, so no screen offers
 * a button the server is certain to refuse.
 */
export default function CancelOrder({
  order,
  layout = 'row',
  onCancelled,
}: {
  order: MyOrder;
  /** `row` sits at the foot of a list card; `block` fills a detail screen. */
  layout?: 'row' | 'block';
  onCancelled?: () => void;
}) {
  const { t } = useT();
  const cancel = useCancelOrder();
  const [asking, setAsking] = useState(false);

  if (!isCancellable(order)) return null;

  const height = layout === 'block' ? 'h-[52px] rounded-[17px]' : 'h-11 rounded-control';

  if (!asking) {
    return (
      <button
        type="button"
        onClick={() => setAsking(true)}
        className={`flex w-full items-center justify-center bg-white text-[15px] font-extrabold text-danger md:text-[16.5px] ${height} ${
          layout === 'row' ? 'border border-danger-line' : ''
        }`}
      >
        {t('Cancel order')}
      </button>
    );
  }

  return (
    <div className="rounded-tile bg-danger-soft px-4 py-3.5" role="group" aria-label={t('Cancel this meal?')}>
      <b className="block text-[15px] text-danger-ink md:text-[16.5px]">{t('Cancel this meal?')}</b>
      <span className="mt-0.5 block text-[13.5px] text-danger-ink md:text-[15px]">
        {t('You can order it again until {when}.', { when: formatCutoffWhen(order.editableUntil) })}
      </span>

      <div className="mt-3 flex gap-2.5">
        <button
          type="button"
          disabled={cancel.isPending}
          onClick={() =>
            cancel.mutate(order.id, {
              onSuccess: () => {
                setAsking(false);
                onCancelled?.();
              },
            })
          }
          className={`flex flex-1 items-center justify-center bg-danger px-3 text-[15px] font-extrabold text-white disabled:opacity-60 md:text-[16.5px] ${height}`}
        >
          {cancel.isPending ? t('Cancelling…') : t('Yes, cancel')}
        </button>
        <button
          type="button"
          disabled={cancel.isPending}
          onClick={() => {
            setAsking(false);
            cancel.reset();
          }}
          className={`flex flex-1 items-center justify-center bg-white px-3 text-[15px] font-extrabold text-ink disabled:opacity-60 md:text-[16.5px] ${height}`}
        >
          {t('Keep it')}
        </button>
      </div>

      {/* The server has the last word — the cutoff may have passed while the
          question was on screen — and when it refuses, it says why. */}
      {cancel.isError && (
        <p className="mt-3 text-[13.5px] font-bold text-danger-ink md:text-[15px]">
          {t(cancel.error.message)}
        </p>
      )}
    </div>
  );
}
